import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiMail,
  FiSettings,
  FiMenu,
  FiX,
  FiBarChart2,
  FiChevronDown,
  FiChevronRight,
  FiTarget,
  FiMessageSquare,
  FiPieChart,
  FiClipboard,
  FiHome,
  FiTrendingUp,
  FiHelpCircle,
  FiActivity,
  FiLayers,
  FiUsers,
  FiVideo,
} from "react-icons/fi";
import { useRole } from "../../hooks/useRole";
import { APP_DISPLAY_NAME } from "../../config/api";
// import ThemeToggle from "./ThemeToggle";

const SideNavbar = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const { isAdmin, isOrgAdmin } = useRole();
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDark, setIsDark] = useState(
    !document.body.classList.contains("light-theme"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(!document.body.classList.contains("light-theme"));
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // Detectar si estamos en móvil
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cerrar sidebar móvil al cambiar de ruta
  useEffect(() => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Función para alternar secciones expandidas
  const toggleSection = (sectionKey) => {
    if (isCollapsed) return; // No expandir si el sidebar está colapsado

    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionKey)) {
      newExpanded.delete(sectionKey);
    } else {
      newExpanded.add(sectionKey);
    }
    setExpandedSections(newExpanded);
  };

  // Estructura del menú jerárquico
  const menuSections = [
    {
      key: "planificacion",
      label: "Planificación",
      icon: <FiTarget />,
      roles: ["admin", "user"],
      isExpandable: true,
      children: [
        {
          path: "/instrumento",
          icon: <FiClipboard />,
          label: "Instrumento",
          isComingSoon: false,
          roles: ["admin", "user"],
        },
        {
          path: "/contacts",
          icon: <FiMail />,
          label: "Contactos",
          isComingSoon: false,
          roles: ["admin", "user"],
        },
        {
          path: "/communication",
          icon: <FiMessageSquare />,
          label: "Comunicaciones",
          isComingSoon: false,
          roles: ["admin", "user"],
        },
      ],
    },
    {
      key: "dashboard",
      path: "/status",
      label: "Estado Avance",
      icon: <FiBarChart2 />,
      roles: ["admin", "user"],
      isExpandable: false,
    },
    {
      key: "resultados",
      path: "/results",
      label: "Resultados",
      icon: <FiPieChart />,
      roles: ["admin", "user"],
      isExpandable: false,
      isComingSoon: false,
    },
  ];

  // Sección inferior: Benchmark | separador | Soporte / Organización
  const bottomTopItems = [
    {
      key: "benchmark",
      path: "/benchmark",
      label: "Benchmark",
      icon: <FiTrendingUp />,
      roles: ["admin", "user"],
      isComingSoon: false,
    },
  ];

  const bottomBottomItems = [
    {
      key: "soporte",
      label: "Soporte",
      icon: <FiHelpCircle />,
      roles: ["admin", "user"],
      isComingSoon: true,
    },
    {
      key: "organizations",
      path: "/users",
      icon: <FiSettings />,
      label: "Organización",
      roles: ["admin", "orgAdmin"],
      isComingSoon: false,
    },
    {
      key: "servicios-admin",
      path: "/admin/servicios",
      icon: <FiLayers />,
      label: "Servicios",
      roles: ["admin"],
      isComingSoon: false,
    },
    {
      key: "usuarios",
      path: "/usuarios",
      icon: <FiUsers />,
      label: "Usuarios",
      roles: ["admin"],
      isComingSoon: false,
    },
    {
      key: "tutoriales",
      path: "/tutoriales",
      icon: <FiVideo />,
      label: "Tutoriales",
      roles: ["admin"],
      isComingSoon: false,
    },
    {
      key: "api-calls",
      path: "/admin/api-calls",
      icon: <FiActivity />,
      label: "Uso API Calls",
      roles: ["admin"],
      isComingSoon: false,
    },
  ];

  // Filtrar elementos según rol
  const filteredSections = menuSections.filter((section) =>
    isAdmin || isOrgAdmin ? true : section.roles.includes("user"),
  );

  return (
    <>
      {/* Sidebar */}
      <div
        className={`sidebar ${isCollapsed ? "collapsed" : ""} ${isMobile && isMobileOpen ? "mobile-open" : ""}`}
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            {!isCollapsed && (
              <img
                src={isDark ? "/LOGOCETIUC_DARK.png" : "/LOGOCETIUC.png"}
                alt={APP_DISPLAY_NAME}
                style={{ height: "32px", width: "auto" }}
              />
            )}
          </div>
          <button
            className="collapse-btn"
            onClick={() => {
              if (isMobile) {
                setIsMobileOpen(!isMobileOpen);
              } else {
                onToggle && onToggle(!isCollapsed);
              }
            }}
          >
            {(isMobile && !isMobileOpen) || (!isMobile && isCollapsed) ? (
              <FiMenu />
            ) : (
              <FiX />
            )}
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-nav">
          {/* Botón de Inicio */}
          <div className="nav-section">
            <div className="nav-item-container">
              <Link
                to="/inicio"
                className={`nav-item ${location.pathname === "/inicio" ? "active" : ""}`}
              >
                <span className="nav-icon">
                  <FiHome />
                </span>
                {!isCollapsed && <span className="nav-label">Inicio</span>}
              </Link>
            </div>
          </div>

          {/* Secciones principales */}
          {filteredSections.map((section) => (
            <div key={section.key} className="nav-section">
              {section.isExpandable ? (
                // Sección expandible
                <>
                  <div
                    className={`nav-item section-header ${
                      section.children?.some(
                        (child) => location.pathname === child.path,
                      )
                        ? "active-section"
                        : ""
                    }`}
                    onClick={() => toggleSection(section.key)}
                  >
                    <span className="nav-icon">{section.icon}</span>
                    {!isCollapsed && (
                      <>
                        <span className="nav-label">{section.label}</span>
                        <span className="expand-icon">
                          {expandedSections.has(section.key) ? (
                            <FiChevronDown />
                          ) : (
                            <FiChevronRight />
                          )}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Sub-elementos */}
                  {!isCollapsed && expandedSections.has(section.key) && (
                    <div className="nav-subitems">
                      {section.children.map((child) => (
                        <div key={child.path} className="nav-subitem-container">
                          {child.isComingSoon ? (
                            <div className="nav-subitem coming-soon">
                              <span className="nav-icon">{child.icon}</span>
                              <span className="nav-label">{child.label}</span>
                              <span className="coming-soon-badge">
                                Próximamente
                              </span>
                            </div>
                          ) : (
                            <Link
                              to={child.path}
                              className={`nav-subitem ${
                                location.pathname === child.path ? "active" : ""
                              }`}
                            >
                              <span className="nav-icon">{child.icon}</span>
                              <span className="nav-label">{child.label}</span>
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // Elemento simple
                <div className="nav-item-container">
                  {section.isComingSoon ? (
                    <div
                      className={`nav-item coming-soon ${
                        location.pathname === section.path ? "active" : ""
                      }`}
                    >
                      <span className="nav-icon">{section.icon}</span>
                      {!isCollapsed && (
                        <>
                          <span className="nav-label">{section.label}</span>
                          <span className="coming-soon-badge">
                            Próximamente
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={section.path}
                      className={`nav-item ${
                        location.pathname === section.path ? "active" : ""
                      }`}
                    >
                      <span className="nav-icon">{section.icon}</span>
                      {!isCollapsed && (
                        <span className="nav-label">{section.label}</span>
                      )}
                    </Link>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Separador antes de Benchmark */}
          <div className="nav-separator">
            <hr />
          </div>

          {/* Benchmark */}
          {bottomTopItems
            .filter((item) =>
              isAdmin || isOrgAdmin ? true : item.roles.includes("user"),
            )
            .map((item) => (
              <div key={item.key} className="nav-section">
                <div className="nav-item-container">
                  {item.isComingSoon ? (
                    <div className="nav-item coming-soon">
                      <span className="nav-icon">{item.icon}</span>
                      {!isCollapsed && (
                        <>
                          <span className="nav-label">{item.label}</span>
                          <span className="coming-soon-badge">
                            Próximamente
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`nav-item ${location.pathname === item.path ? "active" : ""}`}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      {!isCollapsed && (
                        <span className="nav-label">{item.label}</span>
                      )}
                    </Link>
                  )}
                </div>
              </div>
            ))}

          {/* Separador entre Benchmark y Soporte/Organización */}
          <div className="nav-separator">
            <hr />
          </div>

          {/* Soporte / Organización */}
          {bottomBottomItems
            .filter((item) => {
              if (
                item.roles.includes("admin") &&
                !item.roles.includes("orgAdmin") &&
                !item.roles.includes("user")
              ) {
                return isAdmin;
              }
              return isAdmin || isOrgAdmin
                ? item.roles.includes("admin") ||
                    item.roles.includes("orgAdmin")
                : item.roles.includes("user");
            })
            .map((item) => (
              <div key={item.key} className="nav-section">
                <div className="nav-item-container">
                  {item.isComingSoon ? (
                    <div className="nav-item coming-soon">
                      <span className="nav-icon">{item.icon}</span>
                      {!isCollapsed && (
                        <>
                          <span className="nav-label">{item.label}</span>
                          <span className="coming-soon-badge">
                            Próximamente
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`nav-item ${location.pathname === item.path ? "active" : ""}`}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      {!isCollapsed && (
                        <span className="nav-label">{item.label}</span>
                      )}
                    </Link>
                  )}
                </div>
              </div>
            ))}
        </nav>

      </div>

      {/* Overlay for mobile */}
      {isMobile && isMobileOpen && (
        <div
          className="sidebar-overlay active"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
    </>
  );
};

export default SideNavbar;
